/**
 * The chapter card: one hand-lettered mark, on the black between two beats.
 *
 * Spec §7: *"Told **entirely without words.** No dialogue, no subtitles, no
 * narrator. Chapter cards are a **single hand-lettered image** — a bell, a latch,
 * a tally mark — never a sentence."* This is that, and it is the last piece of
 * §7's presentation that had never been built.
 *
 * ── What it is actually fixing ──────────────────────────────────────────────
 *
 * Alex, playing beats 1–3 in order: *"there is not really anything to do to
 * progress and then Rosalie is suddenly in the next room."* The second half of
 * that sentence is this file's problem. A beat ends on black and the successor
 * opens on black — see the handover in `main.ts`, which is a page navigation
 * hidden inside two fades — so the seam is technically invisible and
 * *narratively* invisible too. Nothing marks the passage. She is in a farrowing
 * hall, and then she is in a fattening hall, and the game never says that
 * something happened in between.
 *
 * A card is the cheapest honest answer: it does not explain, it does not name a
 * place, it does not hand the player a sentence. It puts one drawn object on the
 * screen for four seconds and lets the player carry it into the room.
 *
 * ── Why the mark draws itself ───────────────────────────────────────────────
 *
 * Each stroke is a path with `pathLength="1"`, revealed by walking
 * `stroke-dashoffset` from 1 to 0, staggered stroke by stroke. That is not
 * decoration. A symbol that simply appears is a logo; a symbol that is drawn in
 * front of you is somebody's hand, and the whole art direction of this game is
 * the claim that a person made it. It also costs one DOM element and no draw
 * calls, which is why it can exist at all in a room already carrying a
 * draw-call debt.
 *
 * ── Why it is driven from `update`, not from a CSS animation ────────────────
 *
 * The same reason `FadeOverlay` is: a CSS transition runs on wall-clock time and
 * keeps running while the game is paused. A card that finishes itself behind the
 * pause menu, and hands over to a room the player is not looking at, is the kind
 * of bug that only ever reproduces for somebody who pauses.
 */

import { CHAPTER_CARD } from '@/render/palette';
import { chapterMarkForBeat, type ChapterMarkId } from '@/ui/chapterCardDecisions';

export { chapterMarkForBeat, type ChapterMarkId };

/** One drawn stroke of a mark. `d` is an SVG path in a 100 × 100 field. */
interface Stroke {
  d: string;
  /** Relative order this stroke is drawn in, 0-based. Ties draw together. */
  order: number;
}

/**
 * The marks.
 *
 * **One object per beat, and it is an object a stranger can name** (D-236).
 * The first rule here was *"the object the beat is about — not its mood, not its
 * place"*, and it produced a barrel bolt, a cartridge fuse, a wool bale and a
 * culvert headwall: all true to their beats, and all unnamed by a blind viewer.
 * A mark is shown for about three seconds with no caption, so an object nobody
 * carries a picture of cannot be drawn legibly however many times it is redrawn.
 * The rule is now **the most recognisable object that belongs to the beat**, and
 * where no such object exists (beat 10) the beat's time of day stands in for it.
 *
 * **The gate is a blind test, not the author's eye.** Every mark below passed at
 * least one independent viewer who was shown the rendered frame and not told
 * what it was meant to be; the author always sees what they drew. Recipe and
 * results: `docs/traps.md`, *"chapter-card marks are judged blind"*.
 *
 * Deliberately all **stroked and none filled.** A filled shape is a symbol; an
 * outline is a drawing, and it is also what lets every mark reveal itself the
 * same way.
 */
const MARKS: Readonly<Record<ChapterMarkId, readonly Stroke[]>> = {
  /**
   * Beat 2 — an open padlock.
   *
   * The beat is called *The Weakest Latch*, and the mark it had from D-085
   * until D-236 was that latch: a barrel bolt drawn back from its socket. Blind, it
   * was *"an abstract symbol"* and *"a rope"*. The mark needs to say one thing,
   * **it is open**, and the open padlock is the one drawing everybody already
   * reads as exactly that. The farrowing pen has no padlock; this is a sign for
   * the outcome, not a picture of the part.
   *
   * The shackle is lifted clear on its right leg, which is the whole content, so
   * it is drawn after the body; the keyhole lands last.
   */
  padlock: [
    { d: 'M 30,50 L 70,50 C 73,50 74,51 74,54 L 74,86 C 74,89 73,90 70,90 L 30,90 C 27,90 26,89 26,86 L 26,54 C 26,51 27,50 30,50 Z', order: 0 },
    { d: 'M 36,50 L 36,32 C 36,14 64,14 64,32 L 64,38', order: 1 },
    { d: 'M 50,64 C 46,64 46,72 50,72 C 54,72 54,64 50,64 Z M 50,72 L 50,78', order: 2 },
  ],

  /**
   * Beat 3 — a sheaf of straw, tied at the waist.
   *
   * The empty straw pen is the only thing in Act 1 that is given to her rather
   * than taken. The first drawing was five loose stalks lying crossed, and blind
   * it was *"a scribble"* and *"an explosion"*: loose lines are a texture, and a
   * texture is not an object (D-195). A bound sheaf has an outline.
   *
   * **The weakest mark in the set, and knowingly so.** Blind it was *"a sheaf
   * of wheat"* to one viewer and *"an explosion"* to the other, with grain as
   * that viewer's alternative. The stalks fanning up from the band are what
   * make that second reading, and slimmer ears on them made a menorah — see
   * `docs/traps.md` before trying again.
   */
  sheaf: [
    { d: 'M 46,54 C 40,40 32,28 24,16', order: 0 },
    { d: 'M 47,54 C 44,38 40,24 36,12', order: 0 },
    { d: 'M 49,54 C 48,38 46,22 45,8', order: 0 },
    { d: 'M 51,54 C 52,38 54,22 55,8', order: 0 },
    { d: 'M 53,54 C 56,38 60,24 64,12', order: 0 },
    { d: 'M 54,54 C 60,40 68,28 76,16', order: 0 },
    { d: 'M 46,66 C 42,76 36,86 30,94', order: 1 },
    { d: 'M 48,66 C 46,76 44,86 42,94', order: 1 },
    { d: 'M 50,66 L 50,94', order: 1 },
    { d: 'M 52,66 C 54,76 56,86 58,94', order: 1 },
    { d: 'M 54,66 C 58,76 64,86 70,94', order: 1 },
    // The band, last: it is what turns eleven stalks into one thing.
    { d: 'M 40,54 L 60,54 L 60,66 L 40,66 Z', order: 2 },
  ],

  /** Beat 4 — a paw print. Four toes and a pad, and it is facing the player. */
  paw: [
    { d: 'M 26,53 C 20,50 18,42 21,36 C 24,31 30,32 32,39 C 34,46 31,55 26,53 Z', order: 0 },
    { d: 'M 40,44 C 35,40 34,30 38,25 C 42,20 48,23 48,31 C 48,39 45,48 40,44 Z', order: 1 },
    { d: 'M 60,43 C 55,47 51,38 52,30 C 53,23 59,20 62,25 C 66,31 65,39 60,43 Z', order: 2 },
    { d: 'M 74,52 C 69,55 66,46 68,39 C 70,32 76,31 79,36 C 82,42 79,50 74,52 Z', order: 3 },
    // The pad, drawn last and in one stroke, because it is the shape that turns
    // four blobs into an animal having stood there.
    { d: 'M 50,90 C 36,90 26,82 26,72 C 26,60 37,54 50,55 C 64,56 74,63 74,73 C 74,83 63,90 50,90 Z', order: 4 },
  ],

  /**
   * Beat 5 — a hook. One that is empty, and hanging still.
   *
   * The cull corridor's card, and the hardest one to draw honestly: the beat's
   * whole restraint is that the shapes behind the plastic are never resolved
   * (spec §2), so a card that drew what was on the hook would spend in one image
   * what the room spends two minutes refusing to say. An empty hook says the
   * building has hooks in it and says nothing else, and by the time the player
   * sees this card they have already been told the rest by a rail they could not
   * quite see through.
   *
   * The eye is drawn first and the point last, so the stroke order is the same
   * order the eye reads it: hung from something, ending in nothing.
   */
  hook: [
    { d: 'M 42,18 C 42,8 58,8 58,18', order: 0 },
    { d: 'M 50,18 L 50,54', order: 1 },
    { d: 'M 50,54 C 50,76 74,80 76,62 C 77,52 66,49 61,57', order: 2 },
  ],

  /**
   * Beat 6 — a clipboard, with one completed group of five on it.
   *
   * The loading yard's object is not the truck and not the sky. It is the board
   * in Kestner's hands, because **he is frightening because he writes things
   * down** (`docs/beat-6-plan.md` rule 3) and because a tally is the only way
   * this game can say *counting* without a word in it. Spec §7 forbids dialogue
   * and subtitles; five strokes are not language, they are arithmetic, and the
   * player supplies what is being counted from the beat they are about to play.
   *
   * The stroke order is the content. The board arrives, then the clip, then four
   * uprights one at a time — and the fifth stroke, the one that closes the
   * group, lands last and alone. A group of five has just been completed. In
   * five minutes the player will watch him complete another one.
   *
   * The risk in the drawing is that a rectangle with bars in it reads as a
   * **cage**, and the clip is most of what stops it. It is not a reading worth
   * fighting very hard, mind: this is a building where both things are true.
   */
  tally: [
    // The board is wider than it was, and the clip is a real clip — a wide
    // plate with a hole — because the first version's small central nub read as
    // a battery terminal, and blind the whole mark was *"tally marks on a bag"*.
    { d: 'M 24,22 L 76,22 C 79,22 80,23 80,26 L 80,90 C 80,93 79,94 76,94 L 24,94 C 21,94 20,93 20,90 L 20,26 C 20,23 21,22 24,22 Z', order: 0 },
    { d: 'M 32,30 L 32,18 L 42,18 C 42,10 58,10 58,18 L 68,18 L 68,30 Z', order: 1 },
    { d: 'M 46,18 C 46,15 54,15 54,18', order: 1 },
    { d: 'M 36,48 L 36,76', order: 2 },
    { d: 'M 45,48 L 45,76', order: 3 },
    { d: 'M 54,48 L 54,76', order: 4 },
    { d: 'M 63,48 L 63,76', order: 5 },
    // The fifth. It crosses the other four and it is drawn on its own.
    { d: 'M 31,74 L 68,50', order: 6 },
  ],

  /**
   * Beat 7 — a fence post with two strands of barbed wire.
   *
   * The last thing between her and outside. It is allowed to be a threat — beat
   * 4's paw print settled that — and it gives away no solution: she does not get
   * out through the wire.
   *
   * ── Five tries at fibre, then a braid, then this ───────────────────────────
   *
   * The backlog asked for *"a snag of her plush caught on a barb"*, and every
   * version of caught fibre came back a firework, an arrow, a feather or a broom
   * (D-195). The mark that replaced it was the wire alone: two strands braided,
   * one big barb across them. Blind that was *"a chain link"* and *"a beaded
   * chain"*, with barbed wire only as an alternative. The braid was the problem:
   * a twisted pair is what a chain looks like at this size.
   *
   * What makes barbed wire read is **the barbs repeating along a plain strand**,
   * and a post to hang it on, which says fence rather than ornament. Blind this
   * is *"barbed wire on a post"* and *"a fence"*.
   */
  barb: [
    { d: 'M 14,10 L 22,10 L 22,94 L 14,94 Z', order: 0 },
    { d: 'M 22,38 C 50,44 72,44 98,38', order: 1 },
    { d: 'M 22,66 C 50,72 72,72 98,66', order: 2 },
    // The barbs, together and last: they are what the strands are for.
    { d: 'M 44,34 L 54,48 M 54,34 L 44,48 M 74,34 L 84,48 M 84,34 L 74,48', order: 3 },
    { d: 'M 44,62 L 54,76 M 54,62 L 44,76 M 74,62 L 84,76 M 84,62 L 74,76', order: 3 },
  ],

  /**
   * Beat 8 — a light bulb, unlit.
   *
   * *Blackout* begins Act 2 and the lights go out because somebody took them
   * out. The first mark drew the thing that was taken, a cartridge fuse lifted
   * from an empty slot in a board.
   * Blind it was *"a film clapperboard"* and *"a drawer"*. Nobody carries a
   * picture of a cartridge fuse.
   *
   * A bulb is the object everyone knows means light, and drawn with **no rays**
   * it is a light that is off. The backlog's trap was scale — a switch turns off
   * a room, and a fuse a building — and a bulb loses that. Legibility wins.
   *
   * **Upright, not hanging.** A bulb on its flex from a ceiling rose was drawn
   * first and read correctly, but one blind viewer called the cord and socket
   * *"noose-like"*. In this game that reading is disqualifying on its own.
   */
  bulb: [
    { d: 'M 40,66 C 40,58 26,50 26,36 C 26,22 37,12 50,12 C 63,12 74,22 74,36 C 74,50 60,58 60,66 Z', order: 0 },
    { d: 'M 40,66 L 60,66 L 60,82 L 40,82', order: 1 },
    { d: 'M 40,72 L 60,72 M 40,77 L 60,77', order: 1 },
    { d: 'M 44,82 C 44,90 56,90 56,82', order: 2 },
    // The filament, last and cold.
    { d: 'M 45,62 L 45,44 M 55,62 L 55,44 M 45,44 C 46,38 54,38 55,44', order: 3 },
  ],

  /**
   * Beat 9 — sheep shears. The wool shed's own tool.
   *
   * *Wool and Wire* is Holly's beat, and the mark must not be a sheep. The first
   * drawing was a pressed wool bale pinched by two wires; blind it was *"a
   * barrel"* and *"a lantern"*. A ball of wool was tried in its place and came
   * back *"a net"*.
   *
   * Blade shears are the one object in the room with a known outline: a spring
   * bow and two long blades. Blind they are *"shears"* and *"scissors"*, and
   * scissors is close enough. They are a threat to the animal whose beat this
   * is, and like beat 7's wire that is allowed: they give away nothing she does.
   *
   * The short guard bars across the handles look fussy, but a version without
   * them was read as tongs and as a clothes peg. They are what separate grip
   * from blade.
   */
  shears: [
    { d: 'M 28,36 C -4,36 -4,64 28,64', order: 0 },
    { d: 'M 28,36 L 44,39 M 28,64 L 44,61', order: 1 },
    { d: 'M 44,33 L 44,45 M 44,55 L 44,67', order: 2 },
    { d: 'M 44,40 L 98,33 L 46,49 Z', order: 3 },
    { d: 'M 44,60 L 98,67 L 46,51 Z', order: 3 },
  ],

  /**
   * Beat 10 — a crescent moon over two field lines.
   *
   * **The one mark that is the beat's time, not its object**, and that is the
   * exception D-236 names. The beat's object is the culvert. Two sessions drew
   * it — a ring in a splayed headwall, a pipe in an embankment, a tunnel with a
   * path running into it — and blind they were *"a lampshade"*, *"a camera"*,
   * *"a roll of tape"*, *"an umbrella"*, *"a turtle"* and *"a jellyfish"*. A
   * hole in the ground has no outline of its own at this size.
   *
   * The moon is how the whole beat is lit — *"the moonlit roll run"* is in the
   * spec's sentence for it — and it is the first card outdoors at night, after
   * nine that were indoors or at dusk. Blind it is *"a crescent moon over
   * hills"* and *"night sky with moon and sea"*.
   *
   * The fields arrive first and the moon last and alone: the card is the dark
   * coming down on open ground.
   */
  moon: [
    { d: 'M 2,74 C 20,66 40,66 58,72 C 72,76 86,74 98,68', order: 0 },
    { d: 'M 2,90 C 26,82 60,84 98,86', order: 1 },
    { d: 'M 64,12 C 50,14 42,26 44,40 C 46,54 60,62 74,58 C 62,56 54,46 54,34 C 54,24 58,16 64,12 Z', order: 2 },
  ],

  /**
   * Beat 11 — his spectacles, folded.
   *
   * **The beat is called *The Tally* and its mark is not a tally**, because
   * beat 6 already spent that one — and spent it on him, on the board in his
   * hands, with a note above saying *"in five minutes the player will watch him
   * complete another one."* This is that beat. Repeating the mark would turn the
   * card into a label for the title instead of an object from the scene.
   *
   * **It is not the map either, and that is the rule rather than a preference.**
   * The card arrives at the TOP of a beat; beat 5's entry states it — a threat
   * put on the screen before the beat is a spoiler — and the map on his wall is
   * the whole reveal. A card of it would deliver the beat before the beat.
   *
   * So it is the object his one human gesture is about. He takes these off and
   * presses the bridge of his nose, and that gesture is the entire anti-villain
   * lever this beat has (D-187): the close framing gets a face with no features
   * because it is not lit, and the lens is where the eye is allowed to be.
   *
   * **And it is the first card in this game that belongs to a person.** Padlock,
   * sheaf, paw, hook, tally are all the building's. Act 3 is about people, and
   * beat 14's card is a bell, which is Holly's. This is where that register
   * starts.
   *
   * The stroke order is the content, as always: both lenses, then the bridge
   * that joins them, then the temples hinging back — and the glint on the near
   * lens last and alone, because a pair of glasses put down is an object and a
   * glint is the light in the room they were put down in.
   */
  glasses: [
    { d: 'M 18,46 C 18,38 25,34 33,34 C 41,34 48,38 48,46 C 48,54 41,58 33,58 C 25,58 18,54 18,46 Z', order: 0 },
    { d: 'M 57,46 C 57,38 64,34 72,34 C 80,34 87,38 87,46 C 87,54 80,58 72,58 C 64,58 57,54 57,46 Z', order: 1 },
    { d: 'M 48,42 C 50,36 55,36 57,42', order: 2 },
    // ── The temples go BACK, and the first version drew a mouth ──────────────
    //
    // They were authored as two long strokes sweeping under both lenses, the way
    // folded arms actually lie. The frame came back as **a cartoon face**: two
    // eyes and a smile, which is the one reading this beat cannot afford — D-002
    // forbids cute, and the man these belong to must not read as a joke.
    //
    // Anything that closes the space beneath two circles is a mouth. So the
    // temples hinge outward and back instead, and nothing at all is drawn below
    // the lenses.
    { d: 'M 18,43 C 13,39 9,37 5,35', order: 3 },
    { d: 'M 87,43 C 92,39 96,37 99,35', order: 4 },
    // The glint. Last, alone, and inside the near lens.
    { d: 'M 24,41 L 32,37', order: 5 },
  ],

  /**
   * Beat 12 — an ear tag. `docs/beat-12-plan.md`.
   *
   * **It belongs to the animals**, the second card that belongs to someone
   * rather than to the building (beat 11's glasses were the first). It says
   * what a truck at dawn is for without drawing the truck: a tag is how a pig
   * is counted onto one. And it gives away no solution — the beat's three marks
   * are a junction box, a gate and a herd, and none of them is here.
   *
   * A closed outline, per D-195: the body, the punched hole, and last and
   * alone **the blank where the number is written**. It is a line and
   * not a number, because the number would be a word on the card and because a
   * blank is the point: this beat is the one where they are not loaded.
   */
  tag: [
    // ── The first drawing was a BOTTLE ─────────────────────────────────────
    //
    // A round stud on a neck flaring into a body is a flask with a cap and a
    // label, and the blank line made it the label. A tag is read by its CUT
    // corners and its punched hole, so that is what it is now: a flat body,
    // shoulders cut to a point, the hole below the point. No neck, no stud.
    { d: 'M 34,34 L 50,14 L 66,34 L 66,84 C 66,88 63,91 59,91 L 41,91 C 37,91 34,88 34,84 Z', order: 0 },
    { d: 'M 45,33 C 45,30 47,28 50,28 C 53,28 55,30 55,33 C 55,36 53,38 50,38 C 47,38 45,36 45,33 Z', order: 1 },
    // The blank.
    { d: 'M 40,66 L 60,66', order: 2 },
  ],

  /**
   * Beat 14 — the bell. Spec §7.14: *"a single bell, hand-drawn. No 'to be
   * continued' text — the image is the promise."* It is the last card and the
   * only one whose object the player will hold.
   *
   * A closed outline, per D-195: the cup, flaring to its lip; the crown loop;
   * the clapper hanging free below the lip, because a bell whose clapper is
   * drawn is a bell that can still ring. Last and alone, **the torn end of the
   * strap** through the loop — it did not come unbuckled.
   */
  bell: [
    { d: 'M 37,36 C 37,26 43,22 50,22 C 57,22 63,26 63,36 L 66,68 C 67,73 70,76 74,78 L 26,78 C 30,76 33,73 34,68 Z', order: 0 },
    { d: 'M 45,22 C 45,15 55,15 55,22', order: 1 },
    { d: 'M 50,78 L 50,83 M 46,86 C 46,83 48,82 50,82 C 52,82 54,83 54,86 C 54,89 52,90 50,90 C 48,90 46,89 46,86 Z', order: 2 },
    { d: 'M 55,18 C 62,16 69,17 75,15 L 73,19 L 77,20 L 75,23', order: 3 },
  ],
};

function hex(colour: number): string {
  return `#${colour.toString(16).padStart(6, '0')}`;
}

/**
 * A card, from the moment it is constructed until `done`.
 *
 * Construct it, `update(frameDt)` it on the render step, and when `done` goes
 * true let the beat's own fade take over. It never blocks simulation — it is
 * shown over a black screen at the top of a beat, where the only thing running
 * is the beat's opening stage waiting for her to walk somewhere.
 */
export class ChapterCard {
  readonly #root: HTMLDivElement;
  readonly #paths: readonly { element: SVGPathElement; start: number; span: number }[];
  #elapsed = 0;
  #disposed = false;

  constructor(mark: ChapterMarkId) {
    const strokes = MARKS[mark];
    if (strokes === undefined || strokes.length === 0) {
      throw new Error(`chapter card: no mark authored for "${mark}"`);
    }

    this.#root = document.createElement('div');
    this.#root.id = 'chapter-card';
    this.#root.setAttribute('aria-hidden', 'true');
    Object.assign(this.#root.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: hex(CHAPTER_CARD.ground),
      opacity: '0',
      pointerEvents: 'none',
      // Above the fade overlay at 5, because the card is what the fade is
      // covering FOR; below the pause overlay, because a player who pauses
      // during a card must still be shown the way out.
      zIndex: '6',
    } satisfies Partial<CSSStyleDeclaration>);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 100 100');
    // Sized off the SHORTER screen axis so the mark is the same size in a
    // portrait phone frame as in a 16:9 one. `vmin` is the only unit here that
    // does not need a resize listener.
    // 46, not the 26 it was first drawn at. At 26 the mark sat in the middle of
    // a very large black field and read as an icon in a menu — the frame was the
    // subject and the drawing was an ornament on it. A chapter card is a page,
    // and the drawing has to be big enough to be the page's whole content.
    Object.assign(svg.style, { width: '46vmin', height: '46vmin', overflow: 'visible' } satisfies Partial<CSSStyleDeclaration>);

    const lastOrder = strokes.reduce((highest, stroke) => Math.max(highest, stroke.order), 0);
    // Each stroke gets its own slice of the draw-in, and the slices overlap by
    // half. Sequential-with-no-overlap reads as a machine ticking through a list;
    // all-at-once reads as a fade. Overlapping is what a hand does.
    const slice = CHAPTER_CARD.timing.in / (lastOrder / 2 + 1);
    const paths: { element: SVGPathElement; start: number; span: number }[] = [];
    for (const stroke of strokes) {
      const element = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      element.setAttribute('d', stroke.d);
      element.setAttribute('fill', 'none');
      element.setAttribute('stroke', hex(CHAPTER_CARD.ink));
      element.setAttribute('stroke-width', '3.1');
      element.setAttribute('stroke-linecap', 'round');
      element.setAttribute('stroke-linejoin', 'round');
      // Declaring the length as 1 lets the dash walk in fractions of a stroke
      // without measuring anything — `getTotalLength()` would work too and would
      // force a layout on every construction for no gain.
      element.setAttribute('pathLength', '1');
      element.setAttribute('stroke-dasharray', '1 1');
      element.setAttribute('stroke-dashoffset', '1');
      svg.appendChild(element);
      paths.push({ element, start: (stroke.order / 2) * slice, span: slice });
    }
    this.#paths = paths;

    this.#root.appendChild(svg);
    document.body.appendChild(this.#root);
  }

  /** True once the card has drawn, held and cleared. */
  get done(): boolean {
    const { in: drawIn, hold, out } = CHAPTER_CARD.timing;
    return this.#elapsed >= drawIn + hold + out;
  }

  /** 0..1, how opaque the card currently is. The caller holds its own fade off until this is 0. */
  get opacity(): number {
    const { in: drawIn, hold, out } = CHAPTER_CARD.timing;
    // The GROUND fades up fast — it is black on black, so there is nothing to
    // see — and the mark is what actually takes `in` seconds to arrive.
    if (this.#elapsed < drawIn + hold) return Math.min(1, this.#elapsed / 0.35);
    return Math.max(0, 1 - (this.#elapsed - drawIn - hold) / out);
  }

  update(frameDt: number): void {
    if (this.#disposed || this.done) return;
    this.#elapsed += frameDt;
    this.#root.style.opacity = this.opacity.toFixed(4);
    for (const path of this.#paths) {
      const progress = path.span <= 0 ? 1 : (this.#elapsed - path.start) / path.span;
      const clamped = progress <= 0 ? 1 : progress >= 1 ? 0 : 1 - progress;
      path.element.setAttribute('stroke-dashoffset', clamped.toFixed(4));
    }
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#root.remove();
  }
}
